import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { AppLayout } from "../../components/layout/Header";
import { useAuth } from "../../js/hooks/useAuth";
import {
  blockHubUser,
  decideEventRequest,
  EVENT_TYPES,
  hideHubEvent,
  listEventRequests,
  listHubBlocks,
  listHubEvents,
  readHubThumbnail,
  saveHubEvent,
  unblockHubUser,
  useTheHubLive,
} from "../../js/theHub";

const EMPTY_EVENT = {
  id: "",
  title: "",
  type: "city",
  date: "",
  time: "",
  location: "",
  address: "",
  phone: "",
  contact: "",
  host: "",
  url: "",
  description: "",
  special: true,
  featured: false,
  thumbnail: "",
  group_id: "",
};

export default function AdminTheHub() {
  const { session } = useAuth();
  const [pulse, setPulse] = useState(0);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [blockId, setBlockId] = useState("");
  const [note, setNote] = useState("");
  useTheHubLive(() => setPulse((n) => n + 1));

  const requests = useMemo(() => listEventRequests(), [pulse]);
  const events = useMemo(() => listHubEvents(), [pulse]);
  const blocks = useMemo(() => listHubBlocks(), [pulse]);

  function update(event) {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  }

  function save(event) {
    event.preventDefault();
    try {
      saveHubEvent({ ...form, id: form.id || undefined, group_id: form.group_id || null }, session);
      setForm(EMPTY_EVENT);
      setNote(form.id ? "Listing updated." : "Listing saved.");
    } catch (err) {
      setNote(err.message);
    }
  }

  function editListing(item) {
    setForm({
      id: item.id,
      title: item.title || "",
      type: item.type || "city",
      date: item.date || "",
      time: item.time || "",
      location: item.location || "",
      address: item.address || "",
      phone: item.phone || "",
      contact: item.contact || "",
      host: item.host || "",
      url: item.url || "",
      description: item.description || "",
      special: Boolean(item.special),
      featured: Boolean(item.featured),
      thumbnail: item.thumbnail || "",
      group_id: item.group_id || "",
    });
    setNote(`Editing “${item.title}”.`);
  }

  return (
    <AppLayout variant="admin">
      <div className="page-header">
        <div>
          <h1>Hub Review</h1>
          <p className="page-lead">
            Program desk only. Add or edit Hub listings (including event URLs), review member
            requests, and pause rare misuse. Sidebar: <strong>Hub Review</strong> · URL{" "}
            <strong>/admin/hub</strong>
          </p>
          <Link className="primary-btn" to="/admin/hub/hall">
            Open Convention Hall
          </Link>
          <Link className="ghost-btn" to="/admin/founders" style={{ marginLeft: "0.6rem" }}>
            Founder’s View
          </Link>
        </div>
      </div>
      {note ? <p className="confirm-banner">{note}</p> : null}

      <section className="desk-panel card" id="hub-listing-editor">
        <h2>Add or edit a listing</h2>
        <p className="muted">
          To set a direct event link, choose <strong>Edit</strong> on a listing below or fill in{" "}
          <strong>Event listing URL</strong> in this form.
        </p>
        <form className="the-hub-form" onSubmit={save} style={{ maxWidth: "36rem" }}>
          <label className="field">
            <span className="field-label">Title</span>
            <input className="field-input" name="title" value={form.title} onChange={update} required />
          </label>
          <label className="field">
            <span className="field-label">Type</span>
            <select className="field-select" name="type" value={form.type} onChange={update}>
              {EVENT_TYPES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Date</span>
            <input className="field-input" type="date" name="date" value={form.date} onChange={update} required />
          </label>
          <label className="field">
            <span className="field-label">Time</span>
            <input className="field-input" name="time" value={form.time} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Location</span>
            <input className="field-input" name="location" value={form.location} onChange={update} placeholder="Venue or room name" />
          </label>
          <label className="field">
            <span className="field-label">Street address</span>
            <input className="field-input" name="address" value={form.address} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Phone</span>
            <input
              className="field-input"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={update}
              placeholder="619-544-1300"
            />
          </label>
          <label className="field">
            <span className="field-label">Contact desk / email</span>
            <input
              className="field-input"
              name="contact"
              value={form.contact}
              onChange={update}
              placeholder="Events desk or email"
            />
          </label>
          <label className="field">
            <span className="field-label">Host</span>
            <input className="field-input" name="host" value={form.host} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Event listing URL</span>
            <input
              className="field-input"
              name="url"
              value={form.url}
              onChange={update}
              placeholder="https://host-site.org/events/this-listing"
            />
          </label>
          <p className="muted">
            Optional. When set, the title opens this exact event page. Otherwise the title uses the host events
            calendar, then the organization site, from the curated source for this room.
          </p>
          <label className="field">
            <span className="field-label">Description</span>
            <textarea className="field-textarea" name="description" rows={3} value={form.description} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Thumbnail (optional)</span>
            <input
              className="field-input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={async (event) => {
                try {
                  const thumbnail = await readHubThumbnail(event.target.files?.[0]);
                  setForm((current) => ({ ...current, thumbnail }));
                } catch (err) {
                  setNote(err.message);
                }
              }}
            />
          </label>
          <label className="the-hub-announce">
            <input type="checkbox" name="special" checked={form.special} onChange={update} />
            Special event (admin-posted)
          </label>
          <label className="the-hub-announce">
            <input type="checkbox" name="featured" checked={form.featured} onChange={update} />
            Featured Event
          </label>
          <button className="primary-btn" type="submit">
            {form.id ? "Update listing" : "Save listing"}
          </button>
          {form.id ? (
            <button type="button" className="ghost-btn" onClick={() => setForm(EMPTY_EVENT)}>
              Cancel edit
            </button>
          ) : null}
        </form>
        <ul className="the-hub-events" style={{ marginTop: "1rem" }}>
          {events.map((item) => (
            <li key={item.id} className="the-hub-event">
              <strong>{item.title}</strong>
              <p>
                {item.date} {item.time} · {item.location}
                {item.special ? " · Special" : ""}
                {item.url ? " · Has event URL" : ""}
                {item.phone ? ` · ${item.phone}` : ""}
              </p>
              <button type="button" className="text-btn" onClick={() => editListing(item)}>
                Edit
              </button>
              <button type="button" className="text-btn" onClick={() => hideHubEvent(item.id, session)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="desk-panel card">
        <h2>Event requests</h2>
        <ul className="admin-suggest-list">
          {requests.map((item) => (
            <li key={item.id} className="card admin-suggest-item">
              <p className="muted">
                {item.from_name} · {item.mode} · {item.status} · {item.date} {item.time}
              </p>
              <p>
                <strong>{item.title}</strong> — {item.location}
                {item.room_tag ? ` · ${item.room_tag}` : ""}
              </p>
              {item.host ? <p className="muted">Host: {item.host}</p> : null}
              {item.description ? <p>{item.description}</p> : null}
              <p>{item.why}</p>
              {item.contact ? <p className="muted">Verify: {item.contact}</p> : null}
              {item.status === "pending" ? (
                <p className="row" style={{ gap: "0.5rem" }}>
                  <button type="button" className="primary-btn" onClick={() => decideEventRequest(item.id, "approved", session)}>
                    Approve
                  </button>
                  <button type="button" className="ghost-btn" onClick={() => decideEventRequest(item.id, "declined", session)}>
                    Decline
                  </button>
                </p>
              ) : null}
            </li>
          ))}
          {!requests.length ? <li className="muted">No requests yet.</li> : null}
        </ul>
      </section>

      <section className="desk-panel card">
        <h2>Pause a member</h2>
        <p className="muted">Rare use. Enter the member’s user id from Users.</p>
        <form
          className="the-hub-mini"
          onSubmit={(event) => {
            event.preventDefault();
            try {
              blockHubUser(blockId.trim(), session);
              setBlockId("");
              setNote("Member paused in The Hub.");
            } catch (err) {
              setNote(err.message);
            }
          }}
        >
          <input className="field-input" value={blockId} onChange={(event) => setBlockId(event.target.value)} placeholder="user id" />
          <button className="ghost-btn" type="submit">
            Pause
          </button>
        </form>
        <ul>
          {blocks.map((row) => (
            <li key={row.user_id}>
              {row.user_id}{" "}
              <button type="button" className="text-btn" onClick={() => unblockHubUser(row.user_id, session)}>
                Restore
              </button>
            </li>
          ))}
        </ul>
      </section>
    </AppLayout>
  );
}
